// Catalog.ID Client — the registration gate (docs/cid-governance.md M12)
// ============================================================================
// A node registers anyone who pays (`open`) or only the people its operator lets
// in (`invitation`): a voucher minted at the node, an invitation a voucher backs,
// or an organization's signed delegate invitation. A node without an active
// certificate of its own always answers `invitation` (D14).
//
// The client FAILS CLOSED. Open registration — a registration started without a
// code, the storefront, and turning a code into an invitation for someone else —
// is offered only when the node explicitly said `open`. Under `invitation`, and
// when the config could not be read at all, the pages offer a code to enter and
// nothing else, and say which of the two it is. The node refuses the same things
// itself (403 `invitation_only`); this only keeps the pages from offering them.
//
// Pure, so the decision is testable without a DOM; the entry page, the two
// registration wizards and the storefront apply it.

import type { RegistrationConfig } from './payment-flow';

type GateInput = Pick<RegistrationConfig, 'registrationMode' | 'fromServer'>;

/** What the entry page and the storefront may offer. */
export interface RegistrationOffer {
    /** Start a personal or organization registration without a code. */
    openRegistration: boolean;
    /** The storefront: buy registration codes. */
    buyCodes: boolean;
    /** Turn a registration code into an invitation addressed to someone else. */
    personalize: boolean;
    /** One plain sentence saying why the rest is not offered; null when open. */
    notice: string | null;
}

/** What a wizard's payment step may offer. */
export interface PaymentStepOffer {
    /** Card checkout through the payment Worker. */
    pay: boolean;
    /** The waived node's free Continue. */
    waivedContinue: boolean;
    /** Enter a registration code instead. */
    redeemCode: boolean;
    /** The link to the storefront. */
    buyCodes: boolean;
    notice: string | null;
}

export const INVITATION_ONLY_NOTICE =
    'This Catalog.ID server registers new members by invitation only. '
    + 'If you were given a registration code or an invitation, enter its code on this page.';

export const CONFIG_UNREACHABLE_NOTICE =
    'We could not reach this Catalog.ID server to find out how it registers new members, '
    + 'so only a registration code or an invitation can be used right now. Try again in a moment.';

function isOpen(config: GateInput): boolean {
    return config.fromServer && config.registrationMode === 'open';
}

function closedNotice(config: GateInput): string {
    return config.fromServer ? INVITATION_ONLY_NOTICE : CONFIG_UNREACHABLE_NOTICE;
}

export function registrationOffer(config: GateInput): RegistrationOffer {
    if (isOpen(config)) {
        return { openRegistration: true, buyCodes: true, personalize: true, notice: null };
    }
    return { openRegistration: false, buyCodes: false, personalize: false, notice: closedNotice(config) };
}

/**
 * On a waived node, does "Continue" redeem the code field instead of registering
 * for free? Whenever the field holds anything: a prefilled or typed code is
 * redeemed (malformed text is refused as such), never skipped — skipping it
 * left the code unredeemed and its bits parked in its lock.
 */
export function waivedContinueUsesCode(codeField: string): boolean {
    return codeField.trim() !== '';
}

export function paymentStepOffer(
    config: GateInput & Pick<RegistrationConfig, 'paymentRequired'>,
): PaymentStepOffer {
    if (!isOpen(config)) {
        return { pay: false, waivedContinue: false, redeemCode: true, buyCodes: false, notice: closedNotice(config) };
    }
    if (!config.paymentRequired) {
        // A waived open node hands registrations out free: nothing to pay and
        // nothing to buy. A code still redeems — its lock holds the gift or the
        // bits it was minted with, released into the new wallet at the print step.
        return { pay: false, waivedContinue: true, redeemCode: true, buyCodes: false, notice: null };
    }
    return { pay: true, waivedContinue: false, redeemCode: true, buyCodes: true, notice: null };
}
